import { useMemo, useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/States";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { useAction, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative } from "@/lib/format";
import { roleLabel, statusLabel } from "@/lib/labels";
import { NewDraftDialog } from "./NewDraft";
import { ValueField, parseValue, valueText, type Definition } from "./value";

interface Resolved {
  key: string;
  scope: string;
  value: unknown;
  version: number;
  source: "WAREHOUSE" | "GLOBAL" | "DEFAULT";
  is_approved: boolean;
  category: string;
  value_type: string;
  par_id?: string;
  owner_role?: string;
  activated_at?: string;
}

interface Version {
  id: string;
  key: string;
  scope: string;
  version: number;
  value: unknown;
  status: string;
  reason: string;
  created_by?: string;
  created_at: string;
  approved_by?: string;
  approval_reason?: string;
  rejection_reason?: string;
  activated_at?: string;
}

type Tab = "current" | "pending";

export function Settings() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("current");
  const [search, setSearch] = useState("");
  // Tanlangan sozlama KALIT bo'yicha saqlanadi va har chizishda
  // ro'yxatdan qayta olinadi: aks holda aktivlashtirishdan keyin
  // oynada eski «Amaldagi qiymat» qolib ketardi.
  const [selectedKey, setSelectedKey] = useState<{ key: string; scope: string } | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [decision, setDecision] = useState<{
    version: Version;
    action: "approve" | "activate" | "reject";
  } | null>(null);

  // Kalit (`sla.manager_decision`) xodimga hech nima demaydi —
  // o'zbekcha tavsif va qiymat TURI ta'riflar ro'yxatida bo'ladi va
  // BITTA so'rovda olinadi. Tur maydonning shaklini belgilaydi:
  // bayroq uchun ikkita tugma, obyekt uchun JSON maydoni.
  const definitions = useList<Definition>("config", "/api/v1/config-definitions", { limit: 200 });
  const byKey = useMemo(
    () => new Map((definitions.data?.items ?? []).map((item) => [item.key, item])),
    [definitions.data],
  );
  const describe = (key: string) => byKey.get(key)?.description_uz ?? "";

  const current = useList<Resolved>("config", "/api/v1/config", {}, { enabled: tab === "current" });
  const pending = useList<Resolved>("config", "/api/v1/config-pending", {}, { enabled: tab === "pending" });
  const decide = useAction<Version, { version: Version; action: string; reason: string }>(
    "config",
    ({ version, action, reason }) => ({
      path: `/api/v1/config/${version.key}/versions/${version.version}/${action}?scope=${version.scope}`,
      method: "POST",
      body: { reason },
    }),
  );

  const canActivate = session.can(PERM.configActivate);
  const needle = search.trim().toLowerCase();

  const allSettings = (tab === "pending" ? pending.data : current.data)?.items ?? [];
  // Oyna HAR CHIZISHDA yangi ro'yxatdan oladi: aktivlashtirishdan
  // keyin «Amaldagi qiymat» o'zgargan bo'lsa shu yerda ko'rinadi.
  const selected = selectedKey
    ? (allSettings.find(
        (row) => row.key === selectedKey.key && row.scope === selectedKey.scope,
      ) ?? null)
    : null;

  const resolvedRows = allSettings.filter(
    (row) =>
      needle
        ? [row.key, row.category, row.owner_role, describe(row.key),
            byKey.get(row.key)?.help_uz, statusLabel(row.category),
            row.owner_role && roleLabel(row.owner_role)].some((value) =>
            value?.toLowerCase().includes(needle),
          )
        : true,
  );

  return (
    <>
      <PageHeader
        title="Biznes sozlamalari"
        hint="Har bir o'zgarish sababi bilan taklif qilinadi, boshqa odam tomonidan tasdiqlanadi va tasdiqlangandan keyin o'zgarmaydi."
        actions={
          <>
            {tab === "pending" && resolvedRows.length > 0 && (
              <span className="lag lag-stale">
                <span className="lag-dot" />
                {resolvedRows.length} parametr tasdiqlanmagan
              </span>
            )}
            {/* Taklif qilish uchun YOZUV vakolati yetarli: tasdiq
                alohida vakolat va uni boshqa odam beradi. */}
            {session.can(PERM.configEdit) && (
              <button type="button" className="btn btn-filled" onClick={() => setDrafting(true)}>
                <Icon name="plus" size={16} />
                Yangi qiymat
              </button>
            )}
          </>
        }
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Nomi, kaliti yoki izohi bo'yicha…"
        onRefresh={() => void (tab === "pending" ? pending.refetch() : current.refetch())}
        refreshing={tab === "pending" ? pending.isFetching : current.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={setTab}
          options={[
            { value: "current", label: "Amaldagi" },
            { value: "pending", label: "Tasdiqlanmagan" },
          ]}
        />
      </Toolbar>

      {tab === "pending" && (
        <div className="notice">
          <Icon name="alert" size={16} />
          <span>
            Bu sozlamalar hali hech kim tomonidan tasdiqlanmagan — ular kodda
            yozilgan standart qiymat bilan ishlayapti. Ishga tushirishdan
            oldin har birini ko'rib chiqib tasdiqlash kerak.
          </span>
        </div>
      )}

      {/* Har bir sozlama O'Z MAYDONIDA ko'rinadi va shu yerda
          tahrirlanadi: avval qiymat faqat matn edi va o'zgartirish
          uchun alohida oynada kalitni ro'yxatdan qayta topish
          kerak bo'lardi. */}
      {(tab === "pending" ? pending.isLoading : current.isLoading) ? (
        <TableSkeleton rows={5} columns={3} />
      ) : (tab === "pending" ? pending.error : current.error) ? (
        <ErrorState
          error={tab === "pending" ? pending.error : current.error}
          onRetry={() => void (tab === "pending" ? pending.refetch() : current.refetch())}
        />
      ) : resolvedRows.length === 0 ? (
        <EmptyState
          icon="settings"
          title={tab === "pending" ? "Hamma parametr tasdiqlangan" : "Sozlama topilmadi"}
          hint={tab === "pending" ? "TZ §20.1 bo'yicha ochiq parametr qolmagan." : undefined}
        />
      ) : (
        <div className="section">
          {resolvedRows.map((row) => (
            <SettingCard
              key={`${row.key}-${row.scope}`}
              row={row}
              definition={byKey.get(row.key)}
              canEdit={session.can(PERM.configEdit)}
              onHistory={() => setSelectedKey({ key: row.key, scope: row.scope })}
              onProposed={() => {
                void current.refetch();
                void pending.refetch();
              }}
            />
          ))}
          <p className="field-hint">{resolvedRows.length} ta sozlama</p>
        </div>
      )}

      <NewDraftDialog
        open={drafting}
        onClose={() => setDrafting(false)}
        onCreated={() => {
          void current.refetch();
          void pending.refetch();
        }}
      />

      <ConfirmDialog
        open={decision !== null}
        title={
          decision?.action === "approve"
            ? "Versiyani tasdiqlash"
            : decision?.action === "activate"
              ? "Versiyani ishga tushirish"
              : "Versiyani rad etish"
        }
        description={
          decision ? (
            <>
              <span className="mono">{decision.version.key}</span> v
              {decision.version.version}
              {decision.action === "activate" &&
                " — eski faol versiya almashtiriladi va qoida darhol kuchga kiradi."}
            </>
          ) : null
        }
        confirmLabel={decision?.action === "reject" ? "Rad etish" : "Tasdiqlash"}
        danger={decision?.action === "reject"}
        requireReason={decision?.action !== "activate"}
        busy={decide.isPending}
        error={decide.error}
        extra={
          decision?.action === "approve" ? (
            <div className="notice">
              <Icon name="shield" size={16} />
              <span>
                O'z taklifingizni o'zingiz tasdiqlay olmaysiz — server buni
                tekshiradi (INV-004 bilan bir xil qoida).
              </span>
            </div>
          ) : null
        }
        onClose={() => {
          setDecision(null);
          decide.reset();
        }}
        onConfirm={(reason) => {
          if (!decision) return;
          decide.mutate(
            { version: decision.version, action: decision.action, reason },
            {
              onSuccess: () => {
                setDecision(null);
                decide.reset();
              },
            },
          );
        }}
      />

      {selected && (
        <VersionHistory
          key={`${selected.key}:${selected.version}`}
          setting={selected}
          description={describe(selected.key)}
          canDecide={canActivate}
          onDecide={(version, action) => setDecision({ version, action })}
          onClose={() => setSelectedKey(null)}
        />
      )}
    </>
  );
}

/**
 * Bitta sozlamaning versiyalar tarixi.
 *
 * Rad etilgan versiya ham ko'rinadi: nima taklif qilingani va nega
 * qabul qilinmagani keyin ko'rinishi kerak.
 */
/**
 * Bitta sozlama — nomi, joriy qiymati MAYDONDA va taklif qilish.
 *
 * Qiymat darhol kuchga KIRMAYDI: saqlash qoralama versiya
 * yaratadi va u tasdiqlanishi kerak (TZ §20.1). Shuning uchun
 * tugma «Saqlash» emas, «Taklif qilish» deb ataladi — aks holda
 * rahbar qiymat o'zgardi deb o'ylab ketardi.
 */
function SettingCard({
  row,
  definition,
  canEdit,
  onHistory,
  onProposed,
}: {
  row: Resolved;
  definition?: Definition;
  canEdit: boolean;
  onHistory: () => void;
  onProposed: () => void;
}) {
  // Ta'rif kelmaguncha tur noma'lum: maydon oddiy matn bo'lib
  // turadi, lekin qiymat baribir KO'RINADI.
  const type = (definition?.value_type ?? (row.value_type as Definition["value_type"])) ?? "STRING";
  const serverText = valueText(type, row.value);

  const [draft, setDraft] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [proposed, setProposed] = useState(false);

  const propose = useAction<Version, { value: unknown; reason: string }>(
    "config",
    ({ value, reason: why }) => ({
      path: `/api/v1/config/${row.key}/versions`,
      method: "POST",
      body: { scope: row.scope, value, reason: why },
    }),
  );

  const text = draft ?? serverText;
  const dirty = draft !== null && draft !== serverText;
  const parsed = parseValue(
    definition ?? fallbackDefinition(row, type),
    text,
  );

  const cancel = () => {
    setDraft(null);
    setReason("");
    propose.reset();
  };

  return (
    <div className="group group-card">
      <div className="group-head">
        <div>
          {/* Sarlavha ostida TEXNIK qator yo'q: kalit, guruh, qiymat
              turi va mas'ul rol xodimni chalg'itardi. Kalit
              `title` da va «Versiyalar tarixi» da qoladi —
              xizmat bilan gaplashganda kerak bo'lsa topiladi. */}
          <div className="group-title" title={row.key}>
            {definition?.description_uz || row.key}
          </div>
          {/* Qamrov FAQAT umumiy bo'lmaganda: «A ombori» uchun
              kiritilgan qiymatni umumiy deb o'ylash mumkin edi. */}
          {row.scope && row.scope !== "GLOBAL" && (
            <div className="group-hint">{row.scope} ombori uchun</div>
          )}
        </div>
        <div className="group-meta">
          {/* `DEFAULT` — qiymat hali TASDIQLANMAGAN. Yashirilmaydi:
              tasdiqlanmagan parametr bilan ishlash boshqa narsa. */}
          <StatusBadge value={row.source} tone={row.source === "DEFAULT" ? "warning" : "success"} />
          {row.version > 0 && <span className="num">v{row.version}</span>}
          <button type="button" className="btn btn-icon" onClick={onHistory} title="Versiyalar tarixi">
            <Icon name="chevronRight" size={16} />
          </button>
        </div>
      </div>

      {/* Oddiy tildagi izoh: qisqa nom («Prognoz davri») yangi
          kelgan xodimga qiymatni noto'g'ri qo'ysa NIMA bo'lishini
          aytmaydi. */}
      {definition?.help_uz && <p className="group-help">{definition.help_uz}</p>}

      <ValueField
        definition={definition ?? fallbackDefinition(row, type)}
        value={text}
        onChange={(next) => {
          setProposed(false);
          setDraft(next);
        }}
        error={dirty && !parsed.ok ? parsed.error : undefined}
        disabled={!canEdit}
      />

      {dirty && (
        <>
          <label className="field">
            <span className="field-label">Sabab (majburiy)</span>
            <textarea
              className="input"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Nega o'zgartirilyapti — auditda shu matn qoladi"
            />
          </label>
          {propose.error && <ErrorState error={propose.error} />}
          <div className="dialog-actions">
            <button type="button" className="btn btn-text" onClick={cancel} disabled={propose.isPending}>
              Qaytarish
            </button>
            <button
              type="button"
              className="btn btn-filled"
              disabled={!parsed.ok || reason.trim() === "" || propose.isPending}
              onClick={() => {
                if (!parsed.ok) return;
                propose.mutate(
                  { value: parsed.value, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      setReason("");
                      setProposed(true);
                      onProposed();
                    },
                  },
                );
              }}
            >
              Taklif qilish
            </button>
          </div>
        </>
      )}

      {proposed && (
        <div className="notice">
          <Icon name="check" size={16} />
          <span>
            Qoralama yaratildi. Qiymat hali o'zgargani yo'q: «Versiyalar
            tarixi» da tasdiqlab, keyin ishga tushirish kerak. Tizimda
            ikkinchi tasdiqlovchi bo'lsa, tasdiqni BOSHQA xodim beradi.
          </span>
        </div>
      )}

      {!canEdit && (
        <p className="field-hint">
          O'zgartirish uchun <code>config.edit</code> vakolati kerak.
        </p>
      )}
      {row.activated_at && !dirty && (
        <p className="field-hint" title={dateTime(row.activated_at)}>
          Joriy qiymat {relative(row.activated_at)} faollashgan.
        </p>
      )}
      {definition?.scope_kind === "WAREHOUSE" && !dirty && (
        <p className="field-hint">
          Bu sozlama ombor kesimida ham o'rnatiladi — boshqa ombor uchun
          «Yangi qiymat».
        </p>
      )}
    </div>
  );
}

/**
 * Ta'rif kelmagan holat uchun eng kam ta'rif.
 *
 * Ro'yxat va ta'riflar ikki so'rov: biri kech kelsa maydon
 * yo'qolib turmasligi kerak.
 */
function fallbackDefinition(row: Resolved, type: Definition["value_type"]): Definition {
  return {
    key: row.key,
    category: row.category,
    par_id: row.par_id,
    owner_role: row.owner_role,
    value_type: type,
    scope_kind: row.scope === "GLOBAL" ? "GLOBAL_ONLY" : "WAREHOUSE",
    is_feature_flag: false,
    default_value: row.value,
    description_uz: "",
    description_ru: "",
  };
}

function VersionHistory({
  setting,
  description,
  canDecide,
  onDecide,
  onClose,
}: {
  setting: Resolved;
  description: string;
  canDecide: boolean;
  onDecide: (version: Version, action: "approve" | "activate" | "reject") => void;
  onClose: () => void;
}) {
  const history = useList<Version>(
    "config",
    `/api/v1/config/${setting.key}/versions`,
    { scope: setting.scope, limit: 50 },
  );

  return (
    <Drawer label="Versiyalar tarixi">
      <div className="drawer-head">
        <div>
          <div className={description ? "dialog-title" : "dialog-title mono"}>
            {description || setting.key}
          </div>
          {/* Kalit SHU YERDA qoladi: ro'yxatdan olib tashlandi,
              lekin xizmat bilan gaplashganda kerak bo'ladi. */}
          <div className="dialog-desc">
            {description && <span className="mono">{setting.key}</span>}
            {setting.scope && setting.scope !== "GLOBAL" ? ` · ${setting.scope} ombori` : ""}
          </div>
        </div>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="drawer-body">
        <div className="section">
          <div className="section-title">Amaldagi qiymat</div>
          <pre className="json-view">{JSON.stringify(setting.value, null, 2)}</pre>
          {!setting.is_approved && (
            <div className="notice">
              <Icon name="alert" size={16} />
              <span>
                Bu qiymat tasdiqlanmagan standart — hali birorta versiya
                faollashtirilmagan.
              </span>
            </div>
          )}
        </div>

        <div className="section">
          <div className="section-title">Versiyalar</div>
          {history.isLoading && <div className="skeleton" style={{ height: 80 }} />}
          {history.data?.items.length === 0 && (
            <p className="state-hint">Hali birorta versiya taklif qilinmagan.</p>
          )}
          {history.data?.items.map((version) => (
            <article key={version.id} className="card version-card">
              <div className="version-head">
                <span className="num">v{version.version}</span>
                <StatusBadge value={version.status} />
                <span style={{ flex: 1 }} />
                <span className="cell-sub" title={dateTime(version.created_at)}>
                  {relative(version.created_at)}
                </span>
              </div>

              <pre className="json-view">{JSON.stringify(version.value, null, 2)}</pre>

              <dl className="kv">
                <dt>Sabab</dt>
                <dd>{version.reason}</dd>
                {version.approval_reason && (
                  <>
                    <dt>Tasdiq izohi</dt>
                    <dd>{version.approval_reason}</dd>
                  </>
                )}
                {version.rejection_reason && (
                  <>
                    <dt>Rad sababi</dt>
                    <dd>{version.rejection_reason}</dd>
                  </>
                )}
              </dl>

              {canDecide && (version.status === "DRAFT" || version.status === "APPROVED") && (
                <div className="version-actions">
                  {version.status === "DRAFT" && (
                    <button
                      type="button"
                      className="btn btn-tonal btn-sm"
                      onClick={() => onDecide(version, "approve")}
                    >
                      Tasdiqlash
                    </button>
                  )}
                  {version.status === "APPROVED" && (
                    <button
                      type="button"
                      className="btn btn-filled btn-sm"
                      onClick={() => onDecide(version, "activate")}
                    >
                      Ishga tushirish
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-text btn-sm"
                    onClick={() => onDecide(version, "reject")}
                  >
                    Rad etish
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </Drawer>
  );
}
